import { z } from "zod";

import {
  inspirationSectionKindSchema,
  registryItemTypeSchema,
} from "../registryCatalog";
import { viewSchema } from "../views";

export const reploOpencodeHarnessToolIconKeyByName = {
  "add-registry-items": "file",
  "capture-html": "search",
  "collect-site-report-evidence": "search",
  "compile-email": "publish",
  "create-registry-preview": "imageUpload",
  "dev-control": "terminal",
  "download-registry-items": "file",
  "extract-branding": "search",
  "find-design-inspiration": "search",
  "get-branding": "search",
  "find-registry-items": "search",
  "import-figma-page": "file",
  "import-site-page": "file",
  "install-staged-registry-items": "file",
  "manage-tabs": "link",
  publish: "publish",
  "publish-email": "publish",
  "set-branding": "file",
  "set-branding-file": "file",
  "update-brand": "file",
  "share-image": "imageUpload",
  "page-audit": "search",
  "show-gallery": "mediaMcp",
  "suggest-followups": "list",
  "upload-registry-items": "publish",
  "upload-asset": "imageUpload",
  "upload-assets": "imageUpload",
  "validate-insights-report": "search",
  "v1-migration-check": "search",
} as const;

export const reploOpencodeHarnessV1MigrationCheckToolInputSchema = z
  .object({})
  .describe("ReploOpencodeHarnessV1MigrationCheckToolInput");

export const reploOpencodeHarnessV1MigrationCheckToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessV1MigrationCheckToolOutput");

export const REPLO_OPENCODE_HARNESS_PUBLISH_TOOL_DESCRIPTION =
  "Publish a site to make changes live. Requires the siteId from list_sites. Builds, packages, uploads, and deploys.";

export const reploOpencodeHarnessPublishToolInputSchema = z
  .object({
    siteId: z
      .uuid()
      .describe(
        "The ID of the site to publish. Get this from list_sites. The tool finds the site's folder on disk itself, so there is nothing else to look up.",
      ),
    scope: z
      .enum(["all", "routes"])
      .describe(
        'Which drafts go live. "all" publishes every page and content entry with unpublished changes — what "publish the site" means. "routes" publishes only the drafts listed in routes and leaves every other page and entry draft unpublished. When the publish prompt names a scope, use it exactly.',
      ),
    routes: z
      .array(z.string())
      .optional()
      .describe(
        'Required when scope is "routes"; ignored when scope is "all". Routes whose unpublished edits go live — pages and content entries alike, e.g. ["/", "/about", "/blog/my-post"] (a content entry\'s route is /<collection>/<slug>). [] publishes only site-wide changes and leaves every page and entry draft as it is. When the publish prompt gives you a list, pass it exactly.',
      ),
  })
  .describe("ReploOpencodeHarnessPublishToolInput");

export const reploOpencodeHarnessPublishToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessPublishToolOutput");

const reploOpencodeHarnessUploadAssetsItemSchema = z
  .object({
    filePath: z
      .string()
      .min(1)
      .optional()
      .describe("Absolute sandbox path, e.g. /workspace/files/logo.png."),
    url: z
      .url()
      .optional()
      .describe(
        "Public URL to fetch the file from. For a file already in the project, including chat attachments, pass its /workspace path as filePath instead.",
      ),
    name: z
      .string()
      .min(1)
      .optional()
      .describe("Display name. Defaults to the file name."),
    altText: z.string().min(1).optional().describe("Alt text for images."),
  })
  .refine((value) => {
    return (value.filePath === undefined) !== (value.url === undefined);
  }, "Give exactly one of filePath or url");

export const reploOpencodeHarnessUploadAssetsToolInputSchema = z
  .object({
    assets: z.array(reploOpencodeHarnessUploadAssetsItemSchema).min(1).max(50),
    kind: z
      .enum(["assets", "fonts"])
      .optional()
      .describe("fonts tags the files as fonts. Default assets."),
    brandName: z
      .string()
      .min(1)
      .optional()
      .describe("Brand slug to tag every file with."),
    addToBrandImagery: z
      .boolean()
      .optional()
      .describe(
        "Also show these images in Branding's Assets section. Pass true when the user says brand kit, brand assets, or brand imagery.",
      ),
  })
  .describe("ReploOpencodeHarnessUploadAssetsToolInput");

export const reploOpencodeHarnessUploadAssetsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessUploadAssetsToolOutput");

export const reploOpencodeHarnessShareImageToolInputSchema = z
  .object({
    filePath: z
      .string()
      .describe(
        "Absolute sandbox path of a PNG, JPEG, WebP, or GIF, for example /tmp/replo-screenshots/header.png.",
      ),
  })
  .describe("ReploOpencodeHarnessShareImageToolInput");

export const reploOpencodeHarnessShareImageToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessShareImageToolOutput");

export const reploOpencodeHarnessImportSitePageToolInputSchema = z
  .object({
    sourceKind: z
      .enum(["url", "htmlFile", "imageFile"])
      .describe(
        "What the user gave you to recreate: a live URL, an HTML file on disk, or a screenshot image on disk.",
      ),
    source: z
      .string()
      .describe(
        "The URL, or the sandbox path of the HTML or PNG file (for a chat attachment, the /workspace/files/... path named in the message).",
      ),
    route: z
      .string()
      .describe(
        'Route for the imported page, for example /pages/summer-sale, written to app/<route>/page.dev.tsx. You choose it from the request: the route the user named ("on /home", "on this page"), else a new one derived from the source page. The page open in Site Builder is context, not a default target.',
      ),
    replaceExisting: z
      .boolean()
      .optional()
      .describe(
        "Default false. Set true when the route already has a page and the user wants the import there: they named that route or asked for it on the page they have open, or you are re-running this tool on the page it imported (for example to bind productIds). Without it, the tool refuses a route that already has a page.",
      ),
    changes: z
      .string()
      .optional()
      .describe(
        "What the user wants different from the source, in their words: 'change the headline to ...', 'remove the FAQ', 'make the buttons green'. Applied during the import. Omit for a plain copy. Ignored for a screenshot (imageFile): make those changes by hand afterwards.",
      ),
    productIds: z
      .array(z.string())
      .optional()
      .describe(
        "Shopify product GIDs the page should sell, when the user already chose them. Detected product cards are bound to these in page order. Ignored for a screenshot (imageFile).",
      ),
    siteDirectory: z
      .string()
      .optional()
      .describe(
        "Absolute or workspace-relative path of the site to write into, e.g. /workspace/dev/website. Only needed when the workspace holds more than one site.",
      ),
    includeHeader: z
      .boolean()
      .optional()
      .describe(
        "Default true. Pass false when this site's root layout already renders its own header, so the page sits under it instead of bringing the source's header along.",
      ),
    includeFooter: z
      .boolean()
      .optional()
      .describe(
        "Default true. Pass false when this site's root layout already renders its own footer. Independent of includeHeader: a site can have one and not the other.",
      ),
  })
  .describe("ReploOpencodeHarnessImportSitePageToolInput");

export const reploOpencodeHarnessImportSitePageToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessImportSitePageToolOutput");

export const reploOpencodeHarnessImportFigmaPageToolInputSchema = z
  .object({
    figmaUrls: z
      .array(z.string())
      .min(1)
      .max(2)
      .describe(
        "The Figma links the user gave for this page, each with its node-id, such as https://www.figma.com/design/<fileKey>/<name>?node-id=1-1331: one frame, or the page's desktop and mobile frames in either order. Phones first: the narrower frame becomes the page and the wider its desktop design. A link to either frame alone imports the page's mobile frame when the file has one. A link without a node-id names a whole file or page, not a frame: ask the user which frame, or find it with figma_get_file_nodes.",
      ),
    route: z
      .string()
      .optional()
      .describe(
        "Only when the user said where the page goes, such as / for the homepage or the page they have open: the page already there is replaced, and the result names it. Leave it out and the page goes on a new route named after the frame, such as /pages/spring-sale.",
      ),
    siteId: z
      .string()
      .optional()
      .describe(
        "Only when the page goes on a site other than the project's default site: that site's id.",
      ),
  })
  .describe("ReploOpencodeHarnessImportFigmaPageToolInput");

export const reploOpencodeHarnessImportFigmaPageToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessImportFigmaPageToolOutput");

export const reploOpencodeHarnessPageAuditToolArgsShape = {
  route: z
    .string()
    .describe("The page route to check, for example /pages/spring-sale."),
  depth: z
    .enum(["ambient", "deep"])
    .optional()
    .describe(
      "Default ambient (~5s): render, console errors, broken media, sideways overflow at both widths, dead buttons, accessibility. deep (~9s) adds external link verification and click probes.",
    ),
  siteDirectory: z
    .string()
    .optional()
    .describe(
      "Absolute or workspace-relative path of the site, e.g. /workspace/dev/website. Only needed when the workspace holds more than one site.",
    ),
};

export const reploOpencodeHarnessPageAuditToolInputSchema = z
  .object(reploOpencodeHarnessPageAuditToolArgsShape)
  .describe("ReploOpencodeHarnessPageAuditToolInput");

export const reploOpencodeHarnessPageAuditToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessPageAuditToolOutput");

const addRegistryItemIdSchema = z
  .string()
  .min(1)
  .describe("Registry item id from find-registry-items.");

export const reploOpencodeHarnessAddRegistryItemsSectionPlacementSchema = z
  .object({
    pagePath: z
      .string()
      .min(1)
      .describe(
        "Absolute path of the page.dev.tsx to add the section to (page.tsx only when the page has no page.dev.tsx).",
      ),
    after: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Exact JSX already in that page to insert after, copied from the file, e.g. <Hero />. Omit to add the section at the end of the page.",
      ),
  })
  .describe("ReploOpencodeHarnessAddRegistryItemsSectionPlacement");

export const reploOpencodeHarnessAddRegistryItemsToolInputSchema = z
  .object({
    siteDirectory: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Absolute path of the site, e.g. /workspace/dev/website. Only needed when the workspace holds more than one site.",
      ),
    stageOnly: z
      .boolean()
      .optional()
      .describe(
        "Download page, section, component, site, and reference items under .tmp/registry/download/<id>/ and return their paths, without installing. Use it to read a template before building your own version.",
      ),
    items: z
      .array(
        z.discriminatedUnion("type", [
          z.object({ type: z.literal("skill"), id: addRegistryItemIdSchema }),
          z.object({ type: z.literal("plan"), id: addRegistryItemIdSchema }),
          z.object({
            type: z.literal("branding"),
            id: addRegistryItemIdSchema,
          }),
          z
            .object({
              type: z.literal("reference"),
              id: addRegistryItemIdSchema,
            })
            .describe("Always staged for reading."),
          z.object({
            type: z.literal("page"),
            id: addRegistryItemIdSchema,
            route: z
              .string()
              .min(1)
              .optional()
              .describe(
                "URL path for the page, e.g. /about. Omit to use the template's route; a taken route gets -2, -3.",
              ),
          }),
          z.object({
            type: z.literal("section"),
            id: addRegistryItemIdSchema,
            placement:
              reploOpencodeHarnessAddRegistryItemsSectionPlacementSchema
                .optional()
                .describe(
                  "Omit to add the section to the Component Library only.",
                ),
          }),
          z.object({
            type: z.literal("component"),
            id: addRegistryItemIdSchema,
          }),
          z.object({
            type: z.literal("site"),
            id: addRegistryItemIdSchema,
            sitePrefix: z
              .string()
              .min(1)
              .optional()
              .describe(
                "Folder under app/ for the site's routes. Defaults to dev/<slug>.",
              ),
          }),
        ]),
      )
      .min(1)
      .max(5)
      .describe("Up to 5 registry items to add in one call."),
  })
  .describe("ReploOpencodeHarnessAddRegistryItemsToolInput");

export const reploOpencodeHarnessAddRegistryItemsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessAddRegistryItemsToolOutput");

const installRegistryItemBaseSchema = z.object({
  id: z.string().min(1).describe("Registry item ID to install."),
});

export const reploOpencodeHarnessInstallRegistryItemsToolInputSchema = z
  .object({
    projectRoot: z
      .string()
      .min(1)
      .nullable()
      .default(null)
      .describe(
        "Absolute or workspace-relative Next.js project root for page, section, component, and site installs. Null defaults to PROJECT_WEBSITE_DIR, then sandbox site-checkout discovery; required on local.",
      ),
    siteId: z
      .uuid()
      .nullable()
      .default(null)
      .describe(
        "Target site for page, section, component, and site installs. The coordinator resolves it to that site's sandbox checkout; null falls back to the project's default site. Ignored when projectRoot is set.",
      ),
    items: z
      .array(
        z.discriminatedUnion("type", [
          installRegistryItemBaseSchema.extend({
            type: z.literal("skill"),
          }),
          installRegistryItemBaseSchema.extend({
            type: z.literal("plan"),
          }),
          installRegistryItemBaseSchema.extend({
            type: z.literal("page"),
            routeDestRel: z
              .string()
              .min(1)
              .nullable()
              .default(null)
              .describe(
                "Project-root-relative destination for the page route file. Null derives it from the staged route, suffixing on collision.",
              ),
          }),
          installRegistryItemBaseSchema.extend({
            type: z.literal("section"),
          }),
          installRegistryItemBaseSchema.extend({
            type: z.literal("component"),
          }),
          installRegistryItemBaseSchema.extend({
            type: z.literal("site"),
            sitePrefix: z
              .string()
              .min(1)
              .nullable()
              .default(null)
              .describe(
                "Route prefix under app/. Null defaults to dev/<slug>.",
              ),
          }),
        ]),
      )
      .min(1)
      .max(5)
      .describe(
        "Deterministic installs: skill, plan, page, site, section, and component. Sections and components go into Component Library without changing pages. Branding and product installs need the agent.",
      ),
  })
  .describe("ReploOpencodeHarnessInstallRegistryItemsToolInput");

// Page, section, and site installs write into a site checkout; skill and plan
// installs land in durable dirs. Shared so the coordinator's checkout
// resolution and the CLI's stay in lockstep.
export function installRegistryItemNeedsSiteCheckout(item: {
  type: string;
}): boolean {
  return (
    item.type === "page" ||
    item.type === "site" ||
    item.type === "section" ||
    item.type === "component"
  );
}

export const reploOpencodeHarnessInstallRegistryItemsToolOutputSchema = z
  .object({
    installed: z.array(
      z.discriminatedUnion("type", [
        z.object({
          type: z.enum(["skill", "plan"]),
          id: z.string().min(1),
          slug: z.string().min(1),
          destination: z
            .string()
            .min(1)
            .describe(
              "Canonical filesystem destination the item was written to.",
            ),
        }),
        z.object({
          type: z.enum(["section", "component"]),
          id: z.string().min(1),
          slug: z.string().min(1),
          componentName: z
            .string()
            .min(1)
            .describe(
              "Installed Component Library filename stem, used to open its preview.",
            ),
        }),
        z.object({
          type: z.enum(["page", "site"]),
          id: z.string().min(1),
          slug: z.string().min(1),
          routeUrl: z
            .string()
            .nullable()
            .describe(
              "Rendered route the lift produced; null when no route file was lifted.",
            ),
        }),
      ]),
    ),
    failed: z
      .array(
        z.object({
          id: z.string().min(1),
          error: z.string().min(1),
          errorClass: z.string().min(1).optional(),
        }),
      )
      .describe(
        "Every requested item appears in exactly one of installed or failed.",
      ),
  })
  .describe("ReploOpencodeHarnessInstallRegistryItemsToolOutput");

export const reploOpencodeHarnessInstallRegistryItemsRpcOutputSchema = z
  .discriminatedUnion("status", [
    reploOpencodeHarnessInstallRegistryItemsToolOutputSchema.extend({
      status: z.literal("installed"),
    }),
    z.object({
      status: z.literal("requires_site"),
      siteId: z.uuid().nullable(),
    }),
    z.object({
      status: z.literal("outcome_unknown"),
    }),
  ])
  .describe("ReploOpencodeHarnessInstallRegistryItemsRpcOutput");

// The registry also stores product, canvas, inspiration-section, and
// curated-stage items, which add-registry-items cannot install.
export const reploOpencodeHarnessInstallableRegistryItemTypeSchema = z
  .enum([
    "site",
    "page",
    "section",
    "component",
    "skill",
    "plan",
    "branding",
    "reference",
  ])
  .describe("ReploOpencodeHarnessInstallableRegistryItemType");

export const reploOpencodeHarnessFindRegistryItemsToolInputSchema = z
  .object({
    id: z.string().optional().describe("Registry item ID to look up."),
    slug: z.string().optional().describe("Registry item slug to look up."),
    type: reploOpencodeHarnessInstallableRegistryItemTypeSchema
      .optional()
      .describe("Only return this kind of item."),
    search: z
      .string()
      .max(200)
      .optional()
      .describe("What the item should do or look like, up to 200 characters."),
    limit: z
      .number()
      .int()
      .min(1)
      .max(20)
      .optional()
      .describe("How many matches to return, 1 to 20. Default 5."),
  })
  .describe("ReploOpencodeHarnessFindRegistryItemsToolInput");

export const reploOpencodeHarnessFindRegistryItemsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessFindRegistryItemsToolOutput");

export const reploOpencodeHarnessFindDesignInspirationToolInputSchema = z
  .object({
    search: z
      .string()
      .max(200)
      .describe(
        "Semantic search query for the design direction — phrase by look and intent (e.g. 'playful maximalist snack brand homepage', 'clinical wellness product page').",
      ),
    pageType: z
      .string()
      .optional()
      .describe(
        "Optional page-type filter for kind: page only (e.g. home-landing, product-detail, collection, pricing, about-story); ignored for kind: section.",
      ),
    kind: z
      .enum(["page", "section"])
      .optional()
      .describe(
        "page (default): full-page design references with a written breakdown. section: cropped pictures of single sections from real landing pages, ranked by how they look against your query; use this when the user asks for ONE section (a hero, a comparison, reviews…).",
      ),
    sectionKind: inspirationSectionKindSchema
      .optional()
      .describe(
        "With kind: section, an optional hard filter on the section's job (Hero, Comparison, Reviews, FAQ, Features, Pricing, Footer, Navigation, Logos, Gallery, CTA, Other). Leave it out to let the visual ranking decide.",
      ),
    imageUrl: z
      .url()
      .optional()
      .describe(
        "With kind: section, a public https image to rank against. It is blended with `search` into one query, so keep `search` to a few words describing the same thing.",
      ),
    limit: z
      .number()
      .int()
      .min(1)
      .max(20)
      .optional()
      .describe(
        "Optional cap on the number of references returned (1-20; default 5 for pages, 3 for sections). Sections come back one per source site.",
      ),
  })
  .describe("ReploOpencodeHarnessFindDesignInspirationToolInput");

export const reploOpencodeHarnessFindDesignInspirationToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessFindDesignInspirationToolOutput");

const reploOpencodeHarnessSuggestFollowupTextSchema = z
  .string()
  .min(1)
  .max(120)
  .describe(
    "A next step curated for this specific business and aimed at a concrete business outcome (revenue, conversion, average order value, retention, or trust). It is sent to the agent as the USER'S OWN message when clicked, so write it in the user's voice: an imperative or first-person request (e.g. 'Add customer reviews to my homepage to build trust'), using 'my'/'the' not 'your', never phrased as the agent addressing or asking the user, and never a question. Becomes both the chip label and the prefilled message, so keep it to roughly 10 words.",
  );

const reploOpencodeHarnessSuggestFollowupItemSchema = z
  .object({
    text: reploOpencodeHarnessSuggestFollowupTextSchema,
    miniappId: z
      .string()
      .optional()
      .describe(
        'When the suggestion centers on one Replo app, that app\'s mini-app id from the app catalog in your system prompt (e.g. "tasks", "insights"). Omit when no single app is the subject.',
      ),
  })
  .describe("One suggested next step.");

export const reploOpencodeHarnessSuggestFollowupsToolArgsShape = {
  suggestions: z
    .array(reploOpencodeHarnessSuggestFollowupItemSchema)
    .min(1)
    .max(3)
    .describe(
      "1-3 suggested next steps, curated for this business and ordered by likely business impact (most valuable first).",
    ),
};

// Transcripts recorded before suggestions carried an app id hold bare strings,
// and older sandboxes still send them — so consumers parse with this lenient
// shape and normalize, while the args shape above is what the model is asked
// to produce.
export const reploOpencodeHarnessSuggestFollowupsToolInputSchema = z
  .object({
    suggestions: z
      .array(
        z.union([
          reploOpencodeHarnessSuggestFollowupItemSchema,
          reploOpencodeHarnessSuggestFollowupTextSchema,
        ]),
      )
      .min(1)
      .max(3),
  })
  .describe("ReploOpencodeHarnessSuggestFollowupsToolInput");

export const reploOpencodeHarnessSuggestFollowupsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessSuggestFollowupsToolOutput");

export const reploOpencodeHarnessGalleryItemKindSchema = z
  .enum(["section", "page", "ad", "brand"])
  .describe("ReploOpencodeHarnessGalleryItemKind");

const reploOpencodeHarnessGalleryItemSchema = z
  .object({
    kind: reploOpencodeHarnessGalleryItemKindSchema.describe(
      "What the card shows: section (a cropped website section), page (a landing page's first screen), ad (an ad creative), brand (a company).",
    ),
    title: z
      .string()
      .min(1)
      .max(160)
      .describe("Short card title, e.g. the section or ad name."),
    caption: z
      .string()
      .max(300)
      .optional()
      .describe(
        "One line on why this example is worth a look: what it does well, in plain words.",
      ),
    source: z
      .string()
      .max(120)
      .optional()
      .describe("Who it is from, e.g. the site or advertiser name."),
    imageUrl: z
      .url({ protocol: /^https?$/u })
      .describe(
        "The picture, copied EXACTLY as the search tool returned it, including anything after a `#`: that suffix is what lets the chat reload the image after its link expires.",
      ),
    videoUrl: z
      .url({ protocol: /^https?$/u })
      .optional()
      .describe("For a video ad, the playable file; imageUrl is its poster."),
    adId: z
      .string()
      .min(1)
      .max(120)
      .optional()
      .describe(
        "Always set for an ad from sightglass_find_ads: that ad's `id`. Ad media links expire within an hour, and this id is the only way the chat can reload the picture and video afterwards.",
      ),
    buildPrompt: z
      .string()
      .max(400)
      .optional()
      .describe(
        "Written in the user's voice, e.g. \"Build an FAQ section like the Ritual one\". Clicking the card puts this text in the user's message box, unsent, for them to edit and send.",
      ),
  })
  .describe("One example in the gallery.");

export const reploOpencodeHarnessShowGalleryToolArgsShape = {
  title: z
    .string()
    .min(1)
    .max(120)
    .describe('Heading above the gallery, e.g. "FAQ sections".'),
  items: z
    .array(reploOpencodeHarnessGalleryItemSchema)
    .min(1)
    .max(6)
    .describe(
      "1-6 examples you have looked at and judged worth showing, best first. Default to 3; go to 6 only when the user asks for more.",
    ),
};

export const reploOpencodeHarnessShowGalleryToolInputSchema = z
  .object(reploOpencodeHarnessShowGalleryToolArgsShape)
  .describe("ReploOpencodeHarnessShowGalleryToolInput");

export type ReploOpencodeHarnessShowGalleryToolInput = z.infer<
  typeof reploOpencodeHarnessShowGalleryToolInputSchema
>;

export const reploOpencodeHarnessShowGalleryToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessShowGalleryToolOutput");

export const reploOpencodeHarnessCollectSiteReportEvidenceToolArgsShape = {
  route: z
    .string()
    .min(1)
    .describe("The single page route to scan in this call."),
  pageBaseUrl: z
    .string()
    .describe("The sandbox dev server origin the routes render on."),
  siteDirectory: z
    .string()
    .describe("Absolute path of the site source root inside the sandbox."),
};

export const reploOpencodeHarnessCollectSiteReportEvidenceToolInputSchema = z
  .object(reploOpencodeHarnessCollectSiteReportEvidenceToolArgsShape)
  .describe("ReploOpencodeHarnessCollectSiteReportEvidenceToolInput");

export const reploOpencodeHarnessCollectSiteReportEvidenceToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessCollectSiteReportEvidenceToolOutput");

export const reploOpencodeHarnessTabTargetSchema = z.discriminatedUnion(
  "type",
  [
    z.object({
      type: z.literal("miniapp"),
      miniappId: z
        .string()
        .min(1)
        .describe(
          'Miniapp ID for the tab target. Use "website-builder" for the user website preview.',
        ),
      miniappRoute: z
        .string()
        .min(1)
        .nullable()
        .describe(
          'Route inside the miniapp tab. For website-builder pages, use a public route like "/collections/wallets"; use null for a miniapp root tab.',
        ),
    }),
  ],
);

export const reploOpencodeHarnessDevControlToolInputSchema = z
  .object({
    command: z
      .enum(["start", "stop"])
      .describe(
        "`start` brings the site's dev server up (also clears a crash); `stop` shuts it down.",
      ),
    siteId: z
      .uuid()
      .describe(
        "The site's ID. Read it from the site's package.json `replo.siteId`, or get it from list_sites.",
      ),
  })
  .describe("ReploOpencodeHarnessDevControlToolInput");

export const reploOpencodeHarnessDevControlToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessDevControlToolOutput");

export const reploOpencodeHarnessOpenAppToolInputSchema = z
  .object({
    route: z
      .string()
      .describe(
        "Public URL path to open in the editor, for example /collections/wallets.",
      ),
    appName: z
      .string()
      .optional()
      .describe(
        "Optional app name when more than one app is rendered in the sandbox.",
      ),
  })
  .describe("ReploOpencodeHarnessOpenAppToolInput");

export const reploOpencodeHarnessOpenAppToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessOpenAppToolOutput");

export const reploOpencodeHarnessManageTabsToolInputSchema = z
  .discriminatedUnion("operation", [
    z.object({
      operation: z
        .literal("open")
        .describe(
          "Open the selected view. By default it opens in the background with a notification dot so the user's current view is not interrupted; set focus to true to switch the user to it.",
        ),
      view: viewSchema.describe("View to open."),
      focus: z
        .boolean()
        .default(false)
        .describe(
          "Whether to request switching the user's active view to this tab. Leave false (the default) to reveal work in the background without interrupting the user. Set true when the user should land on the view: a direct navigation request, or the first reveal of the primary object they asked you to build, edit, or produce (the page or site you are working on, a drawing, a spreadsheet). The editor keeps it in the background unless the user is currently focused in that chat. Keep it false for later edits and for secondary or unrelated surfaces.",
        ),
    }),
    z.object({
      operation: z.literal("close").describe("Close an existing open tab."),
      tabId: z
        .string()
        .min(1)
        .describe("ID of the open tab to close. Read this from tab context."),
    }),
  ])
  .describe("ReploOpencodeHarnessManageTabsToolInput");

export const reploOpencodeHarnessManageTabsToolArgsShape = {
  operation: z
    .union([
      z
        .literal("open")
        .describe(
          "Open the selected view. By default it opens in the background with a notification dot so the user's current view is not interrupted; set focus to true to switch the user to it.",
        ),
      z.literal("close").describe("Close an existing open tab."),
    ])
    .describe("Operation to perform."),
  view: viewSchema.optional().describe('Required when operation is "open".'),
  focus: z
    .boolean()
    .optional()
    .describe(
      'Only for operation "open". Defaults to false, which opens the tab in the background without stealing focus. Set true to request that the user land on the view: a direct navigation request, or the first reveal of the primary object they asked you to build, edit, or produce. The editor keeps the tab in the background unless the user is currently focused in that chat.',
    ),
  tabId: z
    .string()
    .min(1)
    .optional()
    .describe('Required when operation is "close".'),
};

export const reploOpencodeHarnessManageTabsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessManageTabsToolOutput");

const reploOpencodeHarnessRegistrySelectorSchema = z
  .object({
    entrypointPath: z
      .string()
      .min(1)
      .describe(
        "Project-root-relative path to one registry-publishable file. README.md → site, page.tsx → page, SKILL.md → skill, apps/brand-studio/drafts/<slug>/BUSINESS.md → branding, .plan.md/.plan.mdx or markdown under .plans/ → plan, any other .tsx/.jsx → section. The tool reads the file's frontmatter to derive type, slug, name, category, and tags, auto-expands sites and pages to include children, and includes the full parent directory for skills and branding.",
      ),
    overrides: z
      .object({
        hostRoute: z
          .string()
          .min(1)
          .optional()
          .describe(
            "Override the section or component's preview-capture route. Use when the entry has no enclosing page in the project and needs a standalone preview route.",
          ),
        displayName: z
          .string()
          .min(1)
          .optional()
          .describe(
            "Override the user-facing registry item name for share flows where the caller already knows the clean product name. Does not affect slug, category, tags, or routing.",
          ),
      })
      .optional()
      .describe(
        "Operational overrides the file itself can't describe. Slug, category, and tags live in source frontmatter — edit the file, don't override here.",
      ),
  })
  .describe("ReploOpencodeHarnessRegistrySelector");

export const reploOpencodeHarnessUploadRegistryItemsToolInputSchema = z
  .object({
    projectRoot: z
      .string()
      .min(1)
      .nullable()
      .default(null)
      .describe(
        "Absolute or workspace-relative Next.js project root — the site you're targeting (e.g. `/workspace/dev/<directory>`). In the sandbox, defaults to the active site via PROJECT_WEBSITE_DIR; required on local. All selectors in one call must live under this root.",
      ),
    selectors: z
      .array(reploOpencodeHarnessRegistrySelectorSchema)
      .min(1)
      .max(20)
      .describe(
        "Entrypoint paths the user wants published. The tool auto-expands sites to their pages, and pages to the sections they directly import. The 20-item cap applies to the expanded set.",
      ),
    baseUrl: z
      .url()
      .nullable()
      .default(null)
      .describe(
        "Optional already-running dev-server base URL. Null means the tool decides: in a sandbox, reuse 127.0.0.1:3000; on local, always spawn a fresh server on a random ephemeral port.",
      ),
    visibility: z.enum(["public", "unlisted"]).default("unlisted"),
    types: z
      .array(registryItemTypeSchema)
      .nullable()
      .default(null)
      .describe(
        'Optional allow-list applied AFTER selector expansion. Null = publish everything the selectors expand to. Use e.g. ["page"] to publish just the pages a site expands into, skipping sections and the site row itself. Bundle/preview/upload work is still scoped to the kept items.',
      ),
  })
  .describe("ReploOpencodeHarnessUploadRegistryItemsToolInput");

const reploOpencodeHarnessFailedStylesheetUrlSchema = z.object({
  pageKey: z.string().min(1),
  url: z.url(),
  reason: z.string().min(1),
});

const reploOpencodeHarnessRegistryPreviewResultSchema = z.object({
  artifactPath: z.string().min(1),
  id: z.uuid(),
  canonicalPageKey: z.string().min(1),
  canonicalPreviewUrl: z.url(),
  pages: z.array(
    z.object({
      key: z.string().min(1),
      previewUrl: z.url(),
    }),
  ),
  previewHtmlByteLength: z.number(),
  failedStylesheetUrls: z.array(reploOpencodeHarnessFailedStylesheetUrlSchema),
});

export const reploOpencodeHarnessUploadRegistryItemsToolOutputSchema = z
  .object({
    created: z.array(
      z.object({
        id: z.uuid(),
        type: registryItemTypeSchema,
        slug: z.string().min(1),
        sourceType: z.string().nullable(),
        sourceKey: z.string().nullable(),
        url: z.url(),
        shareUrl: z.url().nullable(),
      }),
    ),
    failed: z.array(
      z.object({
        type: registryItemTypeSchema,
        slug: z.string().min(1),
        error: z.string().min(1),
        errorDetail: z.string().min(1).optional(),
      }),
    ),
    previewResults: z.array(reploOpencodeHarnessRegistryPreviewResultSchema),
    timings: z.object({
      bundleMs: z.number(),
      previewMs: z.number(),
      createMs: z.number(),
      totalMs: z.number(),
    }),
  })
  .describe("ReploOpencodeHarnessUploadRegistryItemsToolOutput");
